import { CKTab, CKTabList, CKTabs } from '@clearkit/react';
import compact from 'lodash/compact';
import flatten from 'lodash/flatten';
import startCase from 'lodash/startCase';

import wrapError from '~/components/JavascriptError/JavascriptErrorHandler';
import SearchTextField from '~/components/SearchTextField';
import { paginateField } from '~/lib/graphql/pagination';

import CompanyEnrichment from './CompanyEnrichment';
import Group from './Group';
import HubspotGroups from './HubspotGroups';
import PersonEnrichment from './PersonEnrichment';
import SalesforceGroups from './SalesforceGroups';
import styles from './styles';

const AttributeBrowser = ({
  classes,
  traits = {},
  enrichment = [],
  hubspotCompanies,
  salesforceAccounts = [],
  salesforceContacts = [],
  salesforceLeads = [],
  onTabChange,
  salesforceAccountConnector,
  object,
}) => {
  const [search, setSearch] = useState('');
  const [tabIndex, setTabIndex] = useState(0);
  const showTraits = Object.keys(traits)?.length > 0;

  const showHubspot = hubspotCompanies?.length > 0;
  const stickyRef = React.useRef();
  const [stickyBounds, setStickyBounds] = React.useState(0);

  React.useLayoutEffect(() => {
    const stickyBounds = stickyRef.current.getBoundingClientRect();
    setStickyBounds(stickyBounds);
  }, [setStickyBounds]);

  const handleTabChange = (index) => {
    onTabChange(stickyBounds);
    setTabIndex(index);
  };

  const salesforceObjects = parseInt(
    salesforceAccounts?.length +
      compact(
        flatten(
          salesforceAccounts?.map((account) =>
            paginateField(account, 'salesforceOpportunities'),
          ),
        ),
      )?.length +
      salesforceContacts.length +
      salesforceLeads.length,
  );

  const hubspotObjects = parseInt(
    hubspotCompanies?.length +
      compact(
        flatten(
          hubspotCompanies?.map((company) => paginateField(company, 'deals')),
        ),
      ).length,
  );

  const showSalesforce = salesforceObjects > 0;

  const showEmployer = object.__typename === 'Person' && object.company;

  return (
    <div className={classnames(classes.root, 'flex-grow')}>
      <CKTabs index={tabIndex} onChange={handleTabChange} isSmall>
        <div className={classes.attributeHeader} ref={stickyRef}>
          <SearchTextField
            className={classes.search}
            placeholder="Search for attributes"
            onSearch={(e) => setSearch(e.target.value)}
          />

          {!search && (
            <CKTabList aria-label="Attribute tabs" className={classes.tabs}>
              {compact([
                <CKTab key="information">Information</CKTab>,
                <CKTab key="traits" style={{ display: !showTraits && 'none' }}>
                  Custom Traits
                </CKTab>,
                <CKTab
                  key="company"
                  style={{ display: showEmployer || 'none' }}>
                  Employer
                </CKTab>,
                <CKTab
                  key="salesforce"
                  badge={salesforceObjects}
                  style={{ display: !showSalesforce && 'none' }}>
                  Salesforce
                </CKTab>,
                <CKTab
                  key="hubspot"
                  badge={hubspotObjects}
                  style={{ display: !showHubspot && 'none' }}>
                  Hubspot
                </CKTab>,
              ])}
            </CKTabList>
          )}
        </div>

        <div className="-mx-4">
          {(search || tabIndex === 0) && (
            <>
              {object.__typename === 'Company' && (
                <CompanyEnrichment enrichment={enrichment} search={search} />
              )}
              {object.__typename === 'Person' && (
                <PersonEnrichment
                  person={object}
                  enrichment={enrichment}
                  search={search}
                />
              )}
            </>
          )}

          {(search || tabIndex === 1) && (
            <Group
              title="Custom Traits"
              sections={[
                Object.keys(traits).map((trait) => ({
                  label: startCase(trait),
                  value: traits[trait],
                })),
              ]}
              type="traits"
              search={search}
            />
          )}

          {showEmployer && (search || tabIndex === 2) && (
            <CompanyEnrichment
              enrichment={object.company?.enrichment}
              search={search}
            />
          )}

          {(search || tabIndex === 3) && salesforceAccountConnector && (
            <SalesforceGroups
              search={search}
              salesforceAccounts={salesforceAccounts}
              salesforceLeads={salesforceLeads}
              salesforceAccountConnector={salesforceAccountConnector}
              salesforceContacts={salesforceContacts}
            />
          )}

          {(search || tabIndex === 4) && (
            <HubspotGroups
              search={search}
              hubspotCompanies={hubspotCompanies}
            />
          )}
        </div>
      </CKTabs>
    </div>
  );
};

AttributeBrowser.propTypes = {
  classes: PropTypes.object,
  enrichment: PropTypes.array,
  traits: PropTypes.object,
  hubspotCompanies: PropTypes.array,
  salesforceAccounts: PropTypes.array,
  onTabChange: PropTypes.func,
  object: PropTypes.shape({
    __typename: PropTypes.string,
  }),
  salesforceContacts: PropTypes.arrayOf(PropTypes.object),
  salesforceLeads: PropTypes.arrayOf(PropTypes.object),
  salesforceAccountConnector: PropTypes.object,
};

export default wrapError(withStyles(styles)(AttributeBrowser));
